import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Pagination,
  Badge,
  Alert,
  Spinner,
  Form,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import axios from "../../services/axios";
import ProjectFeatureMapModalV2 from "./ProjectFeatureMapModalV2";
import CsvUploadModal from "./CsvUploadModal";
import "./ProjectFeatureMapPageV2.scss";

const ProjectFeatureMapPageV2 = () => {
  // State management
  const [maps, setMaps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [sort, setSort] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const initialSortRef = useRef(sort);
  const initialSortDirectionRef = useRef(sortDirection);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Search state - debounced live search
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedMap, setSelectedMap] = useState(null);
  const [modalMode, setModalMode] = useState("create");

  // CSV Upload modal state
  const [showCsvModal, setShowCsvModal] = useState(false);

  // Debounced search effect
  useEffect(() => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0); // Reset to first page when searching
    }, 500);

    // Cleanup
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchInput]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Fetch maps function
  const fetchMaps = useCallback(
    async (isInitialLoad = false) => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }
        setError(null);

        let url = `/domoreport/uploader/allProjectFeatureMaps?limit=${limit}&offset=${
          page * limit
        }`;

        if (sort) {
          const sortDesc = sortDirection === "desc";
          url += `&sortBy=${sort}&desc=${sortDesc}`;
        }

        if (searchTerm && searchTerm.trim() !== "") {
          url += `&search=${encodeURIComponent(searchTerm.trim())}`;
        }

        const response = await axios.get(url);

        setMaps(response.data.projectFeatureMaps || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        console.error("Error fetching project feature maps:", error);
        setError(error.message || "Failed to fetch project feature maps");
      } finally {
        if (isInitialLoad) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [page, sort, sortDirection, limit, searchTerm],
  );

  // Load data on component mount and when dependencies change
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (isFirstLoad) {
      fetchMaps(true);
      setIsFirstLoad(false);
    } else {
      fetchMaps(false);
    }
  }, [fetchMaps, isFirstLoad]);

  // Handle column sort with three-state toggle
  const handleSort = (column) => {
    if (sort === column) {
      // Same column - cycle through asc -> desc -> default
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        // Reset to initial sort
        setSort(initialSortRef.current);
        setSortDirection(initialSortDirectionRef.current);
      }
    } else {
      // New column - start with asc
      setSort(column);
      setSortDirection("asc");
    }
    setPage(0); // Reset to first page when sorting
  };

  // Get sort icon for column header
  const getSortIcon = (column) => {
    if (sort !== column) {
      return <i className="fa fa-sort text-muted" />;
    }
    return sortDirection === "asc" ? (
      <i className="fa fa-sort-asc" />
    ) : (
      <i className="fa fa-sort-desc" />
    );
  };

  // Handle verify action
  const handleVerify = async (id) => {
    try {
      setRefreshing(true);
      await axios.put(`/domoreport/uploader/approveProjectFeatureMap/${id}`);
      setSuccessMessage("Project feature map verified successfully");
      await fetchMaps(false);
    } catch (error) {
      console.error("Error verifying project feature map:", error);
      setError(error.message || "Failed to verify project feature map");
    } finally {
      setRefreshing(false);
    }
  };

  // Handle edit action
  const handleEdit = (map) => {
    setSelectedMap(map);
    setModalMode("edit");
    setShowModal(true);
  };

  // Handle create action
  const handleCreate = () => {
    setSelectedMap(null);
    setModalMode("create");
    setShowModal(true);
  };

  // Handle delete from modal
  const handleDelete = async (id) => {
    await fetchMaps(false);
    setSuccessMessage("Project feature map deleted successfully");
  };

  // Handle save from modal
  const handleSave = async () => {
    await fetchMaps(false);
    setSuccessMessage(
      modalMode === "create"
        ? "Project feature map created successfully"
        : "Project feature map updated successfully",
    );
  };

  // Handle CSV upload success
  const handleCsvUploadSuccess = (summary) => {
    setShowCsvModal(false);
    setSuccessMessage(
      `CSV uploaded: ${summary.recordsCreated} created, ${summary.recordsUpdated} updated`,
    );
    fetchMaps(false);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
  };

  // Pagination calculations
  const totalPages = Math.ceil(total / limit);
  const startEntry = total === 0 ? 0 : page * limit + 1;
  const endEntry = Math.min((page + 1) * limit, total);

  // Generate visible page numbers (max 5 with ellipsis)
  const getVisiblePages = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      const start = Math.max(0, Math.min(page - 2, totalPages - maxVisible));
      const end = Math.min(totalPages, start + maxVisible);

      for (let i = start; i < end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  // Truncate text with tooltip
  const TruncatedText = ({ text, maxLength = 100 }) => {
    if (!text) return <span className="text-muted">—</span>;

    if (text.length <= maxLength) {
      return <span>{text}</span>;
    }

    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
        <span style={{ cursor: "help" }}>
          {text.substring(0, maxLength)}...
        </span>
      </OverlayTrigger>
    );
  };

  return (
    <div className="md-scroll-wrapper">
      <div className="project-feature-map-page-v2 container my-5">
        <Row className="mb-4">
          <Col>
            <h2 className="page-title">
              <i className="fa fa-map-o mr-2" />
              Project Feature Maps
            </h2>
            <p className="text-muted">
              Manage default trade assignments for projects
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            <i className="fa fa-exclamation-circle mr-2" />
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMessage(null)}
          >
            <i className="fa fa-check-circle mr-2" />
            {successMessage}
          </Alert>
        )}

        <Card className="shadow-sm">
          <Card.Body>
            {/* Toolbar */}
            <Row className="mb-3">
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fa fa-search" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by name, description, trade, or UUID..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <Button
                      variant="outline-secondary"
                      onClick={handleClearSearch}
                    >
                      <i className="fa fa-times" />
                    </Button>
                  )}
                </InputGroup>
                {searchTerm && (
                  <small className="text-muted d-block mt-1">
                    <i className="fa fa-filter mr-1" />
                    Filtering by: "{searchTerm}"
                  </small>
                )}
              </Col>
              <Col md={6} className="text-right">
                <Button
                  variant="outline-primary"
                  onClick={() => setShowCsvModal(true)}
                  className="mr-2"
                >
                  <i className="fa fa-upload mr-2" />
                  Upload CSV
                </Button>
                <Button variant="primary" onClick={handleCreate}>
                  <i className="fa fa-plus mr-2" />
                  Create New
                </Button>
              </Col>
            </Row>

            {/* Results info */}
            <Row className="mb-2">
              <Col>
                <small className="text-muted">
                  Showing {startEntry} to {endEntry} of {total} entries
                  {refreshing && (
                    <Spinner
                      animation="border"
                      size="sm"
                      className="ml-2"
                      variant="primary"
                    />
                  )}
                </small>
              </Col>
            </Row>

            {/* Table */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">
                  Loading project feature maps...
                </p>
              </div>
            ) : maps.length === 0 ? (
              <div className="text-center py-5">
                <i className="fa fa-inbox fa-3x text-muted mb-3" />
                <p className="text-muted">
                  {searchTerm
                    ? "No project feature maps found matching your search"
                    : "No project feature maps found"}
                </p>
                {!searchTerm && (
                  <Button variant="primary" onClick={handleCreate}>
                    <i className="fa fa-plus mr-2" />
                    Create First Project Feature Map
                  </Button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="pfm-table">
                  <thead>
                    <tr>
                      <th
                        onClick={() => handleSort("name")}
                        className="sortable"
                      >
                        Name {getSortIcon("name")}
                      </th>
                      <th>Description</th>
                      <th
                        onClick={() => handleSort("trade_id")}
                        className="sortable"
                      >
                        Trade {getSortIcon("trade_id")}
                      </th>
                      <th
                        onClick={() => handleSort("adminVerifiedAt")}
                        className="sortable"
                      >
                        Status {getSortIcon("adminVerifiedAt")}
                      </th>
                      <th className="actions-column">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maps.map((map) => (
                      <tr key={map.id}>
                        <td>
                          <strong>{map.name || "—"}</strong>
                        </td>
                        <td>
                          <TruncatedText
                            text={map.description}
                            maxLength={120}
                          />
                        </td>
                        <td>
                          {map.trade ? (
                            <div className="trade-cell">
                              <span>{map.trade.trade_name}</span>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {map.adminVerifiedAt ? (
                            <Badge className="bg-success text-white">
                              <i className="fa fa-check mr-1" />
                              Verified
                            </Badge>
                          ) : (
                            <Badge className="bg-warning">
                              <i className="fa fa-clock-o mr-1" />
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="btn-group">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(map)}
                              title="Edit"
                            >
                              <i className="fa fa-pencil" />
                            </Button>
                            {!map.adminVerifiedAt && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleVerify(map.id)}
                                title="Verify"
                              >
                                <i className="fa fa-check" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Row className="mt-3">
                <Col className="d-flex justify-content-center">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setPage(0)}
                      disabled={page === 0 || refreshing}
                    />
                    <Pagination.Prev
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0 || refreshing}
                    />

                    {getVisiblePages().map((p) => (
                      <Pagination.Item
                        key={p}
                        active={p === page}
                        onClick={() => setPage(p)}
                        disabled={refreshing}
                      >
                        {p + 1}
                      </Pagination.Item>
                    ))}

                    <Pagination.Next
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1 || refreshing}
                    />
                    <Pagination.Last
                      onClick={() => setPage(totalPages - 1)}
                      disabled={page >= totalPages - 1 || refreshing}
                    />
                  </Pagination>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Modals */}
        <ProjectFeatureMapModalV2
          show={showModal}
          onHide={() => setShowModal(false)}
          map={selectedMap}
          mode={modalMode}
          onSave={handleSave}
          onDelete={handleDelete}
        />

        <CsvUploadModal
          show={showCsvModal}
          onHide={() => setShowCsvModal(false)}
          onSuccess={handleCsvUploadSuccess}
        />
      </div>
    </div>
  );
};

export default ProjectFeatureMapPageV2;
